import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Coins, Gift, HandCoins, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, peso, todayStr } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Four statutory obligations that were computed nowhere.
 *
 * ⚠️ Every figure is read out of the PAYROLL RUNS, which are snapshots. A payslip
 * is never recomputed, so the 13th month here is the arithmetic of what was
 * actually paid — not a fresh calculation against today's rates that would
 * quietly disagree with twelve payslips the guard is holding.
 *
 * ⚠️ "Basic salary" is narrower than gross. PD 851 excludes overtime, rest-day
 * and holiday premiums and night differential, so dividing gross by twelve
 * overpays every guard who ever worked a night. The engine uses regular + leave.
 */
const TABS = [
  ["thirteenth", "13th Month", Gift],
  ["sil", "Service Incentive Leave", CalendarCheck],
  ["bond", "Cash Bond", Coins],
  ["final", "Final Pay", HandCoins],
];

export default function Benefits() {
  const [tab, setTab] = useState("thirteenth");
  const [year, setYear] = useState(Number(todayStr().slice(0, 4)));
  const [thirteenth, setThirteenth] = useState(null);
  const [sil, setSil] = useState(null);
  const [bond, setBond] = useState(null);
  const [guards, setGuards] = useState([]);
  const [guardId, setGuardId] = useState("");
  const [fp, setFp] = useState(null);
  const [refund, setRefund] = useState(null);
  const [rform, setRform] = useState({ amount: "", note: "" });

  const loadYear = (y) => {
    api.get(`/benefits/thirteenth-month?year=${y}`).then((r) => setThirteenth(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/benefits/service-incentive-leave?year=${y}`).then((r) => setSil(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  const loadBond = () =>
    api.get("/benefits/cash-bond").then((r) => setBond(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { loadYear(year); }, [year]);
  useEffect(() => {
    loadBond();
    api.get("/guards").then((r) => setGuards(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!guardId) { setFp(null); return; }
    api.get(`/guards/${guardId}/final-pay`).then((r) => setFp(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [guardId]);

  const doRefund = async () => {
    try {
      await api.post(`/guards/${refund.guard_id}/cash-bond-refund`, {
        amount: Number(rform.amount), note: rform.note,
      });
      toast.success("Refund recorded");
      setRefund(null);
      setRform({ amount: "", note: "" });
      loadBond();
      if (guardId === refund.guard_id) api.get(`/guards/${guardId}/final-pay`).then((r) => setFp(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="page-benefits">
      <PageHeader
        title="Statutory benefits"
        sub="13th month, service incentive leave, the cash bond, and what a leaver is owed"
        actions={
          <Input type="number" data-testid="benefits-year" className="rounded-none w-28 font-mono"
                 value={year} min={2020} max={2100}
                 onChange={(e) => setYear(Number(e.target.value) || year)} />
        }
      />

      <div className="flex flex-wrap border border-border bg-card mb-4" data-testid="benefits-tabs">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-r border-border transition-colors ${
                    tab === k ? "bg-foreground text-background" : "hover:bg-foreground/5 text-muted-foreground"}`}>
            <Icon size={15} weight={tab === k ? "fill" : "regular"} /> {label}
          </button>
        ))}
      </div>

      {tab === "thirteenth" && <Thirteenth data={thirteenth} year={year} />}
      {tab === "sil" && <Sil data={sil} />}
      {tab === "bond" && <Bond data={bond} onRefund={(r) => { setRefund(r); setRform({ amount: String(r.balance), note: "" }); }} />}
      {tab === "final" && (
        <FinalPay guards={guards} guardId={guardId} setGuardId={setGuardId} fp={fp} />
      )}

      <Dialog open={!!refund} onOpenChange={(o) => !o && setRefund(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-heading">
            Refund {refund?.name}&apos;s cash bond
          </DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground mb-1">
            {peso(refund?.deducted)} was deducted over their service and {peso(refund?.refunded)} has been
            given back, so {peso(refund?.balance)} is held. Refunding more than that is not a refund, it is a
            payment, and the server refuses it.
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-bold">Amount</Label>
              <Input type="number" step="0.01" data-testid="refund-amount" className="rounded-none mt-1 h-9 font-mono"
                     value={rform.amount} onChange={(e) => setRform({ ...rform, amount: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">How it was paid</Label>
              <Input data-testid="refund-note" className="rounded-none mt-1 h-9"
                     placeholder="Cash on clearance, cheque no., or the payroll run it goes out on"
                     value={rform.note} onChange={(e) => setRform({ ...rform, note: e.target.value })} />
            </div>
          </div>
          <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="refund-save"
                  disabled={!rform.note.trim() || !Number(rform.amount)} onClick={doRefund}>
            RECORD THE REFUND
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── 13th month ─────────────────────────────────────────────────────────── */

function Thirteenth({ data, year }) {
  const columns = [
    { key: "name", label: "Guard", render: (r) => (
      <div>
        <div className="font-semibold">{r.name}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{r.employee_no} · {r.position || "Security Guard"}</div>
      </div>
    ) },
    { key: "employment_status", label: "Employment", render: (r) => <StatusBadge value={r.employment_status} /> },
    { key: "periods_paid", label: "Periods paid", render: (r) => (
      <div>
        <span className="font-mono text-sm font-bold">{r.periods_paid}</span>
        <div className="text-[10px] text-muted-foreground">{(r.runs || []).slice(-2).join(", ")}</div>
      </div>
    ) },
    { key: "basic_earned", label: "Basic earned", render: (r) => (
      <span className="font-mono tabular-nums">{peso(r.basic_earned)}</span>
    ) },
    { key: "thirteenth_month", label: "13th month due", render: (r) => (
      <div>
        <span className="font-mono tabular-nums font-bold">{peso(r.thirteenth_month)}</span>
        {/*
          A guard who was paid but earned no BASIC is worth explaining rather
          than showing as a zero. It happens when every peso they earned was a
          premium — a cut-off made up of rest-day and holiday work — and PD 851
          excludes all of it. The figure is right and it looks like a fault.
        */}
        {r.periods_paid > 0 && !r.basic_earned && (
          <div className="text-[10px] text-[hsl(var(--warning))] leading-snug max-w-[220px]">
            Paid, but every peso was a premium (rest day, holiday, night or
            overtime), and PD 851 excludes all of it from basic.
          </div>
        )}
      </div>
    ) },
  ];
  const missing = data?.guards_with_no_payslip_this_year || [];

  return (
    <>
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Tile label={`Owed for ${year}`} value={data ? peso(data.total) : "—"} />
        <Tile label="Guards with earnings" value={data?.headcount ?? "—"} />
        <Tile label="Payroll runs counted" value={data?.runs_counted ?? "—"} />
        <Tile label="No payslip this year" value={missing.length} tone={missing.length ? "warning" : null} />
      </div>

      <Basis>
        <span className="text-foreground font-semibold">Total basic salary earned in the year, divided by twelve</span>{" "}
        (PD 851), payable by 24 December. It is not a bonus and it is not discretionary. Overtime, rest-day and
        holiday premiums and night differential are excluded from &quot;basic&quot; — dividing gross by twelve
        overpays every guard who ever worked a night, and is the commonest error in this computation.
        The figures are read from the payslips actually issued, so they agree with what the guard is holding.
      </Basis>

      {missing.length > 0 && (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-3 mb-4 flex gap-2" data-testid="thirteenth-missing">
          <Warning size={18} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <span className="font-bold">{missing.length} guard(s) have no payslip at all in {year}.</span>{" "}
            They compute to nothing here, which is right only if they genuinely earned nothing — a new hire in
            December, or somebody separated in the previous year. A guard who worked and was never run through
            payroll is a different problem, and this is where it shows up:{" "}
            <span className="font-mono">{missing.slice(0, 6).map((m) => m.name).join(", ")}</span>
            {missing.length > 6 ? ` +${missing.length - 6} more` : ""}.
          </div>
        </div>
      )}

      <DataTable columns={columns} rows={data?.rows || []} testid="thirteenth-table"
                 empty={`No payroll run covers ${year} yet, so there is nothing to divide.`} />
    </>
  );
}

/* ── Service incentive leave ────────────────────────────────────────────── */

function Sil({ data }) {
  const rows = data?.rows || [];
  const vested = rows.filter((r) => r.entitled_days).length;
  const notYet = rows.filter((r) => r.entitled_days === 0).length;
  const unknown = rows.filter((r) => r.entitled_days == null).length;

  const columns = [
    { key: "name", label: "Guard", render: (r) => (
      <div>
        <div className="font-semibold">{r.name}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{r.employee_no}</div>
      </div>
    ) },
    { key: "tenure_days", label: "Tenure", render: (r) => (
      r.tenure_days == null
        ? <span className="text-muted-foreground text-xs">unknown</span>
        : <div>
            <span className="font-mono text-sm">{r.tenure_days}d</span>
            <div className="text-[10px] text-muted-foreground">since {fmtDate(r.date_hired)}</div>
          </div>
    ) },
    { key: "entitled_days", label: "Entitled", render: (r) => (
      r.entitled_days == null ? <span className="text-muted-foreground">—</span>
        : <span className="font-mono text-sm font-bold">{r.entitled_days}</span>
    ) },
    { key: "used_days", label: "Used", render: (r) => <span className="font-mono text-sm">{r.used_days}</span> },
    { key: "balance_days", label: "Balance", render: (r) => (
      r.balance_days == null ? <span className="text-muted-foreground">—</span>
        : <span className="font-mono text-sm font-bold">{r.balance_days}</span>
    ) },
    { key: "cash_value", label: "Commutable to cash", render: (r) => (
      r.cash_value == null
        ? <span className="text-muted-foreground text-xs">{r.note}</span>
        : <div>
            <span className="font-mono tabular-nums font-bold">{peso(r.cash_value)}</span>
            {r.note && <div className="text-[10px] text-muted-foreground">{r.note}</div>}
          </div>
    ) },
  ];

  return (
    <>
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Tile label="Cash exposure" value={data ? peso(data.total_cash_exposure) : "—"} tone={data?.total_cash_exposure ? "warning" : null} />
        <Tile label="Vested (1 year+)" value={vested} />
        <Tile label="Not yet vested" value={notYet} />
        <Tile label="No hire date" value={unknown} tone={unknown ? "alert" : null} />
      </div>

      <Basis>
        <span className="text-foreground font-semibold">Five days a year once a guard has a year of service</span>{" "}
        (Labour Code Art. 95), and <span className="text-foreground font-semibold">unused days are commutable
        to cash</span> — the half agencies forget, and the half that turns into a complaint. The balance above is
        not a leave planner: it is money the agency already owes, payable whether the guard takes the days or
        leaves without them.
      </Basis>

      <DataTable columns={columns} rows={rows} testid="sil-table" empty="No guards on file." />
    </>
  );
}

/* ── Cash bond ──────────────────────────────────────────────────────────── */

function Bond({ data, onRefund }) {
  const rows = data?.rows || [];
  const owed = data?.owed_to_leavers || [];

  const columns = [
    { key: "name", label: "Guard", render: (r) => (
      <div>
        <div className="font-semibold">{r.name}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{r.employee_no}</div>
      </div>
    ) },
    { key: "employment_status", label: "Employment", render: (r) => (
      <div>
        <StatusBadge value={r.employment_status} />
        {r.date_separated && <div className="text-[10px] text-muted-foreground mt-0.5">left {fmtDate(r.date_separated)}</div>}
      </div>
    ) },
    { key: "deducted", label: "Deducted", render: (r) => <span className="font-mono tabular-nums">{peso(r.deducted)}</span> },
    { key: "refunded", label: "Refunded", render: (r) => <span className="font-mono tabular-nums">{peso(r.refunded)}</span> },
    { key: "balance", label: "Held", render: (r) => (
      <span className={`font-mono tabular-nums font-bold ${r.balance > 0 ? "" : "text-muted-foreground"}`}>{peso(r.balance)}</span>
    ) },
    { key: "actions", label: "", render: (r) => (
      r.balance > 0 ? (
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                data-testid={`refund-${r.employee_no}`}
                onClick={(e) => { e.stopPropagation(); onRefund(r); }}>
          Refund
        </Button>
      ) : null
    ) },
  ];

  return (
    <>
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <Tile label="Held across the roster" value={data ? peso(data.total_held) : "—"} />
        <Tile label="Guards with a bond" value={rows.length} />
        <Tile label="Owed to leavers" value={data ? peso(data.owed_to_leavers_total) : "—"}
              tone={data?.owed_to_leavers_total ? "alert" : null} />
      </div>

      <Basis>
        A cash bond is a lawful deduction only within limits, and it is{" "}
        <span className="text-foreground font-semibold">refundable</span>. It used to exist in this system as a
        label on a manual deduction with no running balance and nothing that ever gave it back — which is how
        unrefunded bonds become one of the commonest money claims against an agency. The ledger below is built
        from the deductions on the payslips themselves, so it cannot drift from what was taken.
      </Basis>

      {owed.length > 0 && (
        <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-4 flex gap-2" data-testid="bond-owed-banner">
          <Warning size={18} weight="fill" className="text-[hsl(var(--alert))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <span className="font-bold">
              {owed.length} guard{owed.length > 1 ? "s have" : " has"} left and still has a bond balance —{" "}
              {peso(data.owed_to_leavers_total)} in total.
            </span>{" "}
            That money is theirs and is already due. Refund it and record how it was paid.
          </div>
        </div>
      )}

      <DataTable columns={columns} rows={rows} testid="bond-table"
                 empty="No guard has ever had a cash bond deducted. Nothing is held and nothing is owed — which is a clean answer, not a missing one." />
    </>
  );
}

/* ── Final pay ──────────────────────────────────────────────────────────── */

function FinalPay({ guards, guardId, setGuardId, fp }) {
  const sorted = [...guards].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <>
      <div className="border border-border bg-card p-3 mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[280px]">
          <Label className="text-[10px] uppercase font-bold">Guard</Label>
          <Select value={guardId} onValueChange={setGuardId}>
            <SelectTrigger data-testid="final-pay-guard" className="rounded-none mt-1 h-9">
              <SelectValue placeholder="Pick a guard to compute a clearance for" />
            </SelectTrigger>
            <SelectContent>
              {sorted.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name} · {g.employee_no} · {(g.employment_status || "").replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-muted-foreground leading-snug max-w-lg">
          Computed as of the guard&apos;s separation date when they have one, otherwise today. Run it before
          somebody leaves, not after — the blockers are the only leverage the agency has left once the final
          cheque is signed.
        </div>
      </div>

      {!fp && (
        <div className="border border-border p-6 text-xs text-muted-foreground" data-testid="final-pay-empty">
          Pick a guard. The clearance shows the 13th month earned to date, the leave balance in cash, the bond
          to be given back, and anything still signed out to them.
        </div>
      )}

      {fp && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="final-pay-sheet">
          <div className="lg:col-span-2 border border-border bg-card">
            <div className="border-b border-border p-4">
              <div className="font-heading font-bold text-lg">{fp.guard.name}</div>
              <div className="text-xs text-muted-foreground font-mono">
                {fp.guard.employee_no} · {fp.guard.position || "Security Guard"} · hired {fmtDate(fp.guard.date_hired)}
                {fp.tenure_days != null ? ` · ${fp.tenure_days} day(s) of service` : ""}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                As of <span className="font-mono">{fp.as_of}</span>
                {fp.guard.date_separated ? " (separation date)" : " (today — they have no separation date on file)"}
                {fp.guard.separation_reason ? ` · ${fp.guard.separation_reason}` : ""}
              </div>
            </div>

            <Line label="13th month" amount={fp.thirteenth_month.amount}
                  detail={`${peso(fp.thirteenth_month.basic_earned)} basic earned over ${fp.thirteenth_month.periods.length} period(s) / 12`} />
            <Line label="Service incentive leave" amount={fp.service_incentive_leave.amount}
                  detail={`${fp.service_incentive_leave.balance_days} of ${fp.service_incentive_leave.entitled_days} day(s) unused at ${peso(fp.service_incentive_leave.daily_rate)}/day`} />
            <Line label="Cash bond refund" amount={fp.cash_bond.amount}
                  detail={`${peso(fp.cash_bond.deducted)} deducted, ${peso(fp.cash_bond.refunded)} already returned`} />

            <div className="flex items-center justify-between border-t-2 border-foreground p-4">
              <span className="font-heading font-bold uppercase tracking-wider text-sm">Gross final pay</span>
              <span className="font-mono tabular-nums font-black text-2xl">{peso(fp.gross_final_pay)}</span>
            </div>

            {fp.thirteenth_month.periods.length > 0 && (
              <div className="border-t border-border p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
                  Payslips the 13th month is built from
                </div>
                {fp.thirteenth_month.periods.map((p) => (
                  <div key={p.run} className="flex items-center justify-between border-b border-border py-1 text-[11px]">
                    <span className="font-mono">{p.run} · {p.period}</span>
                    <span className="font-mono tabular-nums">basic {peso(p.basic)} · net {peso(p.net)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className={`border p-4 ${fp.clear_to_release ? "border-[hsl(var(--success))]" : "border-[hsl(var(--alert))]"}`}
                 data-testid="final-pay-clearance">
              <div className={`font-heading font-bold text-sm uppercase tracking-wider ${
                fp.clear_to_release ? "text-[hsl(var(--success))]" : "text-[hsl(var(--alert))]"}`}>
                {fp.clear_to_release ? "Clear to release" : "Not clear to release"}
              </div>
              {fp.blockers.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-2 leading-snug">
                  Nothing is signed out to this guard. The money above can be released against a signed
                  clearance.
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  {fp.blockers.map((b, i) => (
                    <div key={i} className="text-xs leading-snug border-l-2 border-[hsl(var(--alert))] pl-2">
                      <span className="font-bold uppercase text-[10px] tracking-wider">{b.kind}</span>
                      <div>{b.detail}</div>
                    </div>
                  ))}
                  <p className="text-[11px] text-muted-foreground leading-snug pt-1">
                    An agency that pays the final cheque before the firearm comes back has lost the only
                    leverage it had, and the unit is on the agency&apos;s licence, not the guard&apos;s. Book
                    the return in the Armoury first.
                  </p>
                </div>
              )}
            </div>

            {fp.trailing_approved_shifts.length > 0 && (
              <div className="border border-[hsl(var(--warning))] p-4" data-testid="final-pay-trailing">
                <div className="font-heading font-bold text-sm uppercase tracking-wider text-[hsl(var(--warning))]">
                  {fp.trailing_approved_shifts.length} shift(s) after the last cut-off
                </div>
                <div className="mt-2 space-y-1">
                  {fp.trailing_approved_shifts.map((s) => (
                    <div key={s.ref_no} className="text-[11px] flex items-center justify-between border-b border-border py-1">
                      <span className="font-mono">{s.ref_no}</span>
                      <span className="text-muted-foreground">{s.date} · {s.site_name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug mt-2">{fp.trailing_note}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ── shared bits ────────────────────────────────────────────────────────── */

function Line({ label, amount, detail }) {
  return (
    <div className="flex items-start justify-between border-b border-border p-4">
      <div>
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-[11px] text-muted-foreground">{detail}</div>
      </div>
      <span className="font-mono tabular-nums font-bold">{peso(amount)}</span>
    </div>
  );
}

function Basis({ children }) {
  return (
    <div className="border border-border bg-card p-3 mb-4 text-xs text-muted-foreground leading-relaxed max-w-4xl">
      {children}
    </div>
  );
}

function Tile({ label, value, tone }) {
  const border = tone === "alert" ? "border-[hsl(var(--alert))]"
    : tone === "warning" ? "border-[hsl(var(--warning))]"
      : tone === "success" ? "border-[hsl(var(--success))]" : "border-border";
  return (
    <div className={`border ${border} bg-card p-3`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="font-heading font-black text-2xl tabular-nums">{value}</div>
    </div>
  );
}
